import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../../api/client';
import { getToken } from '../../utils/auth';
import { thumbUrl, type CyberpunkPhoto } from '../../api/types';
import adminStyles from '../../pages/Admin.module.css';
import styles from './CyberpunkPhotoManager.module.css';

interface Props {
  /** 冒泡操作结果到 Admin 顶部消息条 */
  onMessage: (type: 'success' | 'error', text: string) => void;
}

function formatBytes(bytes: number): string {
  if (!bytes) return '-';
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}

/** File → 纯 base64（剥离 data URL 前缀） */
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      const idx = result.indexOf(',');
      resolve(idx >= 0 ? result.slice(idx + 1) : result);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

/** 读取图片真实分辨率（登记到数据库，画廊 HUD 展示用） */
function readDimensions(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
      URL.revokeObjectURL(url);
    };
    img.onerror = () => {
      resolve({ width: 0, height: 0 });
      URL.revokeObjectURL(url);
    };
    img.src = url;
  });
}

export default function CyberpunkPhotoManager({ onMessage }: Props) {
  const [photos, setPhotos] = useState<CyberpunkPhoto[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [editDate, setEditDate] = useState('');
  const [editSort, setEditSort] = useState(0);
  const [confirmId, setConfirmId] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.listAllCyberpunkPhotos(getToken()!);
      setPhotos(data);
    } catch (err) {
      onMessage('error', err instanceof Error ? err.message : '加载图库失败');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    let ok = 0;
    let fail = 0;
    for (const file of Array.from(files)) {
      try {
        if (!file.type.startsWith('image/')) {
          fail += 1;
          continue;
        }
        const [base64, dim] = await Promise.all([fileToBase64(file), readDimensions(file)]);
        await api.uploadCyberpunkPhoto(
          {
            filename: file.name,
            content_type: file.type,
            data_base64: base64,
            width: dim.width,
            height: dim.height,
          },
          getToken()!,
        );
        ok += 1;
      } catch {
        fail += 1;
      }
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
    onMessage(
      fail === 0 ? 'success' : 'error',
      `上传完成：成功 ${ok}，失败 ${fail}${fail > 0 ? '（仅支持 jpeg/png/webp/gif，≤7MB）' : ''}`,
    );
    load();
  };

  const startEdit = (photo: CyberpunkPhoto) => {
    setEditingId(photo.id);
    setEditTitle(photo.title);
    setEditDesc(photo.description);
    setEditDate(photo.diary_date || '');
    setEditSort(photo.sort_order);
    setConfirmId(null);
  };

  const saveEdit = async (photo: CyberpunkPhoto) => {
    try {
      await api.updateCyberpunkPhoto(
        photo.id,
        {
          title: editTitle,
          description: editDesc,
          // 空串 = 清空日记日期（后端回退展示入库日期）
          diary_date: editDate,
          sort_order: editSort,
        },
        getToken()!,
      );
      onMessage('success', `已更新「${photo.filename}」`);
      setEditingId(null);
      load();
    } catch (err) {
      onMessage('error', err instanceof Error ? err.message : '更新失败');
    }
  };

  const toggleVisibility = async (photo: CyberpunkPhoto) => {
    const next = photo.status === 'active' ? 'hidden' : 'active';
    try {
      await api.updateCyberpunkPhoto(photo.id, { status: next }, getToken()!);
      onMessage('success', `「${photo.filename}」已${next === 'active' ? '上架' : '下架'}`);
      load();
    } catch (err) {
      onMessage('error', err instanceof Error ? err.message : '状态更新失败');
    }
  };

  const handleDelete = async (photo: CyberpunkPhoto) => {
    if (confirmId !== photo.id) {
      setConfirmId(photo.id);
      return;
    }
    setConfirmId(null);
    try {
      await api.deleteCyberpunkPhoto(photo.id, getToken()!);
      onMessage('success', `「${photo.filename}」已删除（COS 对象保留）`);
      load();
    } catch (err) {
      onMessage('error', err instanceof Error ? err.message : '删除失败');
    }
  };

  return (
    <>
      <div className={adminStyles.toolbar}>
        <button
          className={adminStyles.btnPrimary}
          disabled={uploading}
          onClick={() => fileInputRef.current?.click()}
        >
          {uploading ? 'UPLOADING...' : '+ 上传照片（可多选）'}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          multiple
          className={styles.hiddenFileInput}
          onChange={(e) => handleUpload(e.target.files)}
        />
        <span className={adminStyles.count}>
          {photos.length} 张（含隐藏/删除）
        </span>
        <button className={adminStyles.btn} onClick={load} disabled={loading}>
          {loading ? 'LOADING...' : '↻ 刷新'}
        </button>
      </div>

      {loading && <div className={adminStyles.loadingRow}>LOADING ARCHIVE...</div>}

      {!loading && photos.length === 0 && (
        <div className={adminStyles.loadingRow}>档案为空 — 点击「上传照片」添加第一批记录</div>
      )}

      {!loading && photos.length > 0 && (
        <div className={styles.photoGrid}>
          {photos.map((photo) => {
            const isEditing = editingId === photo.id;
            const isDeleted = photo.status === 'deleted';
            return (
              <div
                key={photo.id}
                className={`${styles.photoCard} ${isDeleted ? styles.photoCardDeleted : ''}`}
              >
                <div className={styles.thumbWrap}>
                  <img className={styles.thumb} src={thumbUrl(photo)} alt={photo.filename} loading="lazy" />
                </div>

                <div className={styles.photoMeta}>
                  <div className={styles.badgeRow}>
                    <span
                      className={`${styles.badge} ${
                        isDeleted
                          ? styles.badgeDeleted
                          : photo.status === 'active'
                            ? styles.badgeActive
                            : styles.badgeHidden
                      }`}
                    >
                      {isDeleted ? '已删除' : photo.status === 'active' ? '已上架' : '已下架'}
                    </span>
                    {photo.source === 'mcp' && <span className={`${styles.badge} ${styles.badgeMcp}`}>MCP</span>}
                  </div>

                  {isEditing ? (
                    <div className={styles.editRow}>
                      <input
                        className={styles.editInput}
                        value={editTitle}
                        placeholder="日记标题（可选）"
                        onChange={(e) => setEditTitle(e.target.value)}
                      />
                      <textarea
                        className={`${styles.editInput} ${styles.editTextarea}`}
                        value={editDesc}
                        rows={4}
                        placeholder="日记正文（点击卡片打开时展示的画面文案）"
                        onChange={(e) => setEditDesc(e.target.value)}
                      />
                      <label className={styles.editLabel}>
                        日记日期
                        <input
                          className={styles.editInput}
                          type="date"
                          value={editDate}
                          onChange={(e) => setEditDate(e.target.value)}
                        />
                      </label>
                      <input
                        className={styles.editInput}
                        type="number"
                        value={editSort}
                        min={0}
                        onChange={(e) => setEditSort(Number(e.target.value) || 0)}
                      />
                      <div className={styles.actionRow}>
                        <button className={adminStyles.btnSmall} onClick={() => saveEdit(photo)}>保存</button>
                        <button className={adminStyles.btnSmall} onClick={() => setEditingId(null)}>取消</button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className={styles.metaName}>{photo.title || photo.filename}</div>
                      {photo.description && (
                        <div className={styles.diaryExcerpt}>
                          {photo.description.length > 40
                            ? `${photo.description.slice(0, 40)}…`
                            : photo.description}
                        </div>
                      )}
                      <div className={styles.metaSub}>
                        <span>
                          {photo.width > 0 ? `${photo.width}×${photo.height}` : '分辨率未登记'}
                        </span>
                        <span>{formatBytes(photo.size_bytes)}</span>
                        <span>排序 #{photo.sort_order}</span>
                      </div>
                      <div className={styles.metaSub}>
                        <span>日记日期 {photo.diary_date || photo.created_at.slice(0, 10)}</span>
                      </div>
                      <div className={styles.metaSub}>
                        <span>by {photo.uploaded_by || '-'}</span>
                        <span>{new Date(photo.created_at).toLocaleDateString()}</span>
                      </div>
                    </>
                  )}

                  <div className={styles.actionRow}>
                    {!isEditing && !isDeleted && (
                      <>
                        <button className={adminStyles.btnSmall} onClick={() => startEdit(photo)}>编辑</button>
                        <button className={adminStyles.btnSmall} onClick={() => toggleVisibility(photo)}>
                          {photo.status === 'active' ? '下架' : '上架'}
                        </button>
                      </>
                    )}
                    {!isEditing && isDeleted && (
                      <button className={adminStyles.btnSmall} onClick={() => toggleVisibility(photo)}>恢复</button>
                    )}
                    {!isEditing && (
                      <button
                        className={`${adminStyles.btnSmall} ${adminStyles.btnDanger}`}
                        onClick={() => handleDelete(photo)}
                      >
                        {confirmId === photo.id ? '确认删除？' : '删除'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}
